// Type the number: the anion gap or the corrected calcium, worked out from the bloods slip.
// A big pad (digits, a point only when the question takes decimals, delete) with the unit fixed on screen; the pad
// refuses what the question would not accept, and the lock-in sends { value }. It docks at the foot of the screen, like
// a phone's own keyboard, compact enough to leave the monitor or the slip above it in view; the slip scrolls in the room
// above the pad (dock.ts), so every row can be brought clear of it. At the reveal: the true value beside yours, the
// formula, how the room answered and how it scored.
import { useCallback, useEffect, useMemo, useState, type ReactNode, type Ref } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { Delete, Lock } from 'lucide-react';
import type { NumberSpec } from '@shared/types';
import { Stem } from '@/components/Stem';
import { Button } from '@/components/Button';
import { GraphicCard } from '@/clinical/GraphicCard';
import { haptic } from '@/lib/haptics';
import type { FormatViewProps } from './types';
import { phaseFor } from './layout';
import { useAnswerSubmit } from './parts';
import { canSubmit, displayEntry, entryProblem, formatNumber, hasDecimalKey, hasSignKey, keyFromKeyboard, keypadSpec, pressKey, type KeypadKey, type KeypadSpec } from './numberInput';
import { creditFor, type Credit } from './credit';
import { CreditSplit, NumberHistogram } from './reveal';
import { useNearing } from './useServerNow';
import { useDockRoom } from './dock';
import { Split, useWide } from './split';

type PublicNumberSpec = Omit<NumberSpec, 'answer'>;

/** The question's number settings, with safe defaults when a field is missing. */
export function numberSpecOf(q: FormatViewProps['question']): PublicNumberSpec {
  const n = q.number;
  const pad = keypadSpec(n);
  return {
    label: n?.label?.trim() || 'Your answer', unit: n?.unit ?? '', decimals: pad.decimals, min: pad.min, max: pad.max,
    tolerance: Math.max(0, Number(n?.tolerance) || 0), span: Math.max(0, Number(n?.span) || 0), formula: n?.formula,
  };
}

export function NumberView(props: FormatViewProps) {
  const { question: q, myAnswer, reveal, display = 'phones' } = props;
  const spec = useMemo(() => numberSpecOf(q), [q]);
  const mine = typeof myAnswer?.value === 'number' ? myAnswer.value : undefined;
  const phase = phaseFor(props.phase, mine !== undefined, reveal);
  const { submit, live } = useAnswerSubmit({ ...props, phase });
  const screen = display === 'screen';
  const revealed = phase === 'revealed' && reveal ? reveal : undefined;
  const credit = revealed ? creditFor(q, revealed, myAnswer) : undefined;
  const graphic = q.graphic;
  // The stem usually names the value ("Work out the anion gap."): then the pad keeps that name for screen readers only.
  // In classroom mode the stem is on the big screen, so the pad shows the name.
  const named = !screen && Boolean(q.stem) && q.stem.toLowerCase().includes(spec.label.toLowerCase());
  // On a phone the pad is docked and the graphic gets the room above it, bounded from the pad's measured height.
  // On a desktop question nothing docks: the graphic sits on the left, the pad on the right.
  const wide = useWide(display);
  const docked = !screen && Boolean(graphic) && !revealed && !wide;
  const dock = useDockRoom(docked);

  return (
    <div ref={dock.wrap} className="fx-format" data-format="number" data-phase={phase} data-display={display} data-dock={docked ? '' : undefined} data-layout={wide ? 'split' : undefined}>
      <Split wide={wide}
        left={<>
          {!screen && q.stem && <Stem text={q.stem} />}
          {!screen && graphic && (
            <div ref={dock.room} className="fx-dock-room">
              <GraphicCard graphic={graphic} stem={q.stem} revealed={Boolean(revealed)} deadlineAt={revealed ? undefined : q.deadlineAt} enlargeable />
            </div>
          )}
          {screen && <p className="sr-only">The question is on the big screen. Answer here.</p>}
        </>}
        right={revealed ? (
          <NumberResult spec={spec} reveal={revealed} mine={mine} credit={credit} />
        ) : (
          <NumberPad spec={spec} live={live} locked={mine} docked={!screen && !wide} padRef={dock.dock} deadlineAt={q.deadlineAt} nameShown={!named} onSubmit={(value) => submit({ value })} />
        )} />
    </div>
  );
}

const KEYS: ReadonlyArray<KeypadKey> = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

export function NumberPad({ spec, live, locked, docked = false, padRef, deadlineAt, nameShown = true, onSubmit }: {
  spec: PublicNumberSpec; live: boolean; locked?: number; docked?: boolean; deadlineAt?: number;
  /** The pad's element, for whoever sizes the room above it. */
  padRef?: Ref<HTMLElement>;
  /** Show the value's name above the readout (false when the stem already names it; screen readers still hear it). */
  nameShown?: boolean;
  onSubmit: (value: number) => void;
}) {
  const reduce = useReducedMotion() ?? false;
  const pad: KeypadSpec = useMemo(() => ({ decimals: spec.decimals, min: spec.min, max: spec.max }), [spec.decimals, spec.min, spec.max]);
  const [text, setText] = useState('');
  // A refused key nudges the display (each refusal replays it) and says the limit until the next key that counts.
  const [nudge, setNudge] = useState(0);
  const [refused, setRefused] = useState(false);
  const problem = entryProblem(text, pad);
  const ready = live && canSubmit(text, pad);
  // A typed number that is not locked in scores nothing: in the last seconds the button says so.
  const late = useNearing(deadlineAt, 5000, ready);

  const press = useCallback((key: KeypadKey) => {
    if (!live) return;
    const r = pressKey(text, key, pad);
    if (r.refused) { setNudge((n) => n + 1); setRefused(true); }
    else { setText(r.text); setRefused(false); }
    haptic('tap');
  }, [live, pad, text]);
  const send = useCallback(() => {
    if (!live || !canSubmit(text, pad)) return;
    onSubmit(Number(text));
  }, [live, text, pad, onSubmit]);

  // Desktop players can type: digits, a point or comma, minus, Backspace, Escape to clear, Enter to lock in.
  useEffect(() => {
    if (!live) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === 'Enter') { e.preventDefault(); send(); return; }
      const k = keyFromKeyboard(e.key);
      if (k) { e.preventDefault(); press(k); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [live, press, send]);

  const isLocked = locked !== undefined;
  const shown = isLocked ? formatNumber(locked!, spec.decimals) : displayEntry(text);
  const hint = isLocked ? undefined
    : problem === 'low' ? `From ${formatNumber(spec.min, spec.decimals)} to ${formatNumber(spec.max, spec.decimals)}`
    : refused ? `Up to ${formatNumber(spec.max, spec.decimals)}${spec.decimals ? `, ${spec.decimals === 1 ? 'one decimal place' : `${spec.decimals} decimal places`}` : ''}`
    : undefined;
  const spoken = isLocked ? `${spec.label}: ${shown}${spec.unit ? ` ${spec.unit}` : ''}, locked in.` : text ? `${spec.label}: ${shown}${spec.unit ? ` ${spec.unit}` : ''}` : `${spec.label}: nothing typed yet`;

  return (
    <section ref={padRef} className="fx-pad" data-docked={docked ? '' : undefined} data-locked={isLocked ? '' : undefined} aria-label="Number pad">
      <div className="fx-pad-top">
        <motion.div key={`nudge-${nudge}`} className="fx-pad-display" data-empty={!isLocked && !text ? '' : undefined} data-warn={problem === 'low' ? '' : undefined}
          initial={nudge > 0 && !reduce ? { x: 0 } : false} animate={nudge > 0 && !reduce ? { x: [0, -7, 6, -4, 2, 0] } : { x: 0 }} transition={{ duration: 0.32 }}>
          <span className="fx-pad-label">
            <span className="fx-pad-name">{isLocked && <Lock size={12} strokeWidth={2.6} aria-hidden="true" />}<span className={nameShown ? undefined : 'sr-only'}>{spec.label}</span></span>
            {hint && <span className="fx-pad-hint">{hint}</span>}
          </span>
          <span className="fx-pad-readout">
            <span className="fx-pad-value">{shown || '0'}</span>
            {!isLocked && live && <span className="fx-pad-caret" aria-hidden="true" />}
            {spec.unit && <span className="fx-pad-unit">{spec.unit}</span>}
          </span>
          <span className="sr-only" aria-live="polite">{spoken}</span>
        </motion.div>
        {!isLocked && <Button variant="primary" size="lg" disabled={!ready} onClick={send} className={`fx-pad-go w-auto${late ? ' fx-go-late' : ''}`}>Lock in</Button>}
      </div>
      <AnimatePresence initial={false}>
        {!isLocked && (
          <motion.div key="keys" className="fx-pad-keys" role="group" aria-label="Keys" initial={false}
            exit={reduce ? { opacity: 0 } : { opacity: 0, height: 0 }} transition={{ duration: 0.22 }}>
            {KEYS.map((k) => <PadKey key={k} k={k} disabled={!live} onPress={press} />)}
            {hasDecimalKey(pad) ? <PadKey k="." label="." spoken="Decimal point" disabled={!live} onPress={press} />
              : hasSignKey(pad) ? <PadKey k="-" label="−" spoken="Minus" disabled={!live} onPress={press} />
              : <span className="fx-key-gap" aria-hidden="true" />}
            <PadKey k="0" disabled={!live} onPress={press} />
            <PadKey k="del" spoken="Delete" disabled={!live || !text} onPress={press} label={<Delete size={22} strokeWidth={2.2} aria-hidden="true" />} />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function PadKey({ k, label, spoken, disabled, onPress }: { k: KeypadKey; label?: ReactNode; spoken?: string; disabled?: boolean; onPress: (k: KeypadKey) => void }) {
  return (
    <button type="button" className="fx-key" data-key={k} aria-label={spoken ?? k} disabled={disabled} onClick={() => onPress(k)}>
      {label ?? k}
    </button>
  );
}

/**
 * The answer view's number card: the true value, yours and what it earned, the formula, the room's answers and scores.
 * `room` (the stage) leaves out the player's own card.
 */
export function NumberResult({ spec, reveal, mine, credit, compact = false, room = false }: {
  spec: PublicNumberSpec; reveal: NonNullable<FormatViewProps['reveal']>; mine?: number; credit?: Credit; compact?: boolean; room?: boolean;
}) {
  const answer = reveal.numberAnswer;
  const unit = spec.unit ? <span className="fx-result-unit">{spec.unit}</span> : null;
  return (
    <section className="fx-result" data-compact={compact ? '' : undefined} aria-label="The answer">
      <div className="fx-result-top" data-room={room ? '' : undefined}>
        <div className="fx-result-cell" data-kind="answer" data-state="correct">
          <span className="fx-result-cap">The answer</span>
          <span className="fx-result-num">{typeof answer === 'number' ? formatNumber(answer, spec.decimals) : '?'}{unit}</span>
        </div>
        {!room && (
          <div className="fx-result-cell" data-kind="mine" data-credit={credit}>
            <span className="fx-result-cap">You</span>
            {mine !== undefined
              ? <span className="fx-result-num">{formatNumber(mine, spec.decimals)}{unit}</span>
              : <span className="fx-result-none">No answer</span>}
          </div>
        )}
      </div>
      {spec.formula && (
        <p className="fx-formula"><span className="fx-formula-cap">Formula</span><span className="fx-formula-text">{spec.formula}</span></p>
      )}
      {Array.isArray(reveal.histogram) && reveal.histogram.length > 0 && typeof answer === 'number' && (
        <NumberHistogram buckets={reveal.histogram} answer={answer} mine={mine} decimals={spec.decimals} unit={spec.unit} />
      )}
      {reveal.credit && <CreditSplit credit={reveal.credit} mine={!room && mine !== undefined ? credit : undefined} />}
    </section>
  );
}
